import React, { useId } from 'react';

export type SensorVariant = 'photoelectric' | 'inductive' | 'capacitive' | 'light_curtain';

export interface SensorSvgProps {
  variant?: SensorVariant;
  detected?: boolean;
  range?: number; // Detection beam / range length in px (default: 80)
  color?: string; // Optional custom body accent color
  className?: string;
  style?: React.CSSProperties;
}

export const SensorSvg: React.FC<SensorSvgProps> = ({
  variant = 'photoelectric',
  detected = false,
  range = 80,
  color = '#0284c7',
  className = '',
  style,
}) => {
  const uid = useId().replace(/:/g, '_');
  const effectiveRange = Math.max(40, Number(range) || 80);
  const svgW = variant === 'light_curtain' ? 84 : 44;
  const svgH = Math.max(60, effectiveRange + 44);

  // Status colors
  const outLedColor = detected ? '#f59e0b' : '#334155'; // Amber when triggered
  const pwrLedColor = '#22c55e'; // Steady green power LED

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${svgW} ${svgH}`}
      width={svgW}
      height={svgH}
      className={`industrial-sensor sensor-${variant} ${className}`}
      style={{ overflow: 'visible', ...style }}
    >
      <defs>
        {/* Glow filter for active laser beam and indicators */}
        <filter id={`laserGlow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Stainless steel cylindrical gradient for Inductive barrel */}
        <linearGradient id={`steelBarrelGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="25%" stopColor="#94a3b8" />
          <stop offset="50%" stopColor="#f8fafc" />
          <stop offset="75%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Brass locknut gradient */}
        <linearGradient id={`brassNutGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#78350f" />
          <stop offset="30%" stopColor="#fde047" />
          <stop offset="70%" stopColor="#d97706" />
          <stop offset="100%" stopColor="#451a03" />
        </linearGradient>

        {/* Sensor Housing 3D Gradient */}
        <linearGradient id={`sensorBodyGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="30%" stopColor="#1e293b" />
          <stop offset="85%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>

        {/* Laser Beam Gradient (translucent with bright red center) */}
        <linearGradient id={`laserBeamGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ef4444" stopOpacity="0.9" />
          <stop offset="40%" stopColor="#ef4444" stopOpacity={detected ? '0.35' : '0.75'} />
          <stop offset="100%" stopColor="#ef4444" stopOpacity={detected ? '0.1' : '0.9'} />
        </linearGradient>

        {/* Light Curtain Yellow Housing Gradient */}
        <linearGradient id={`curtainPoleGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#854d0e" />
          <stop offset="25%" stopColor="#facc15" />
          <stop offset="65%" stopColor="#eab308" />
          <stop offset="100%" stopColor="#713f12" />
        </linearGradient>
      </defs>

      {/* ================= 1. PHOTOELECTRIC BEAM SENSOR ================= */}
      {variant === 'photoelectric' && (
        <g id="photoelectric-sensor">
          {/* Mounting Bracket & Base */}
          <rect x="8" y="0" width="28" height="6" rx="1.5" fill="#334155" stroke="#0f172a" strokeWidth="0.8" />
          <circle cx="12" cy="3" r="1.5" fill="#94a3b8" />
          <circle cx="32" cy="3" r="1.5" fill="#94a3b8" />

          {/* Main Sensor Head Body (Rectangular Industrial Housing) */}
          <rect
            x="10"
            y="6"
            width="24"
            height="32"
            rx="3"
            fill={`url(#sensorBodyGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="1"
          />

          {/* Color Accent Brand Stripe */}
          <rect x="10" y="14" width="24" height="4" fill={color} />

          {/* Lens Barrel at the bottom facing downwards towards conveyor */}
          <rect x="16" y="38" width="12" height="4" rx="1" fill="#0f172a" stroke="#475569" strokeWidth="0.8" />
          {/* Optical Glass Lens */}
          <ellipse cx="22" cy="42" rx="4" ry="1.5" fill="#0284c7" />

          {/* Status LEDs on Top/Front Face */}
          {/* PWR LED (Green) */}
          <circle cx="16" cy="24" r="2" fill={pwrLedColor} filter={`url(#laserGlow-${uid})`} />
          {/* OUT / DETECTED LED (Amber) */}
          <circle
            cx="28"
            cy="24"
            r="2.2"
            fill={outLedColor}
            filter={detected ? `url(#laserGlow-${uid})` : 'none'}
          />
          {/* Sensitivity Adjustment Potentiometer */}
          <circle cx="22" cy="31" r="2.5" fill="#0f172a" stroke="#64748b" strokeWidth="0.6" />
          <line x1="20.5" y1="31" x2="23.5" y2="31" stroke="#cbd5e1" strokeWidth="0.6" />

          {/* Text Labels */}
          <text x="16" y="20" fill="#94a3b8" fontSize="4" fontFamily="monospace" textAnchor="middle">PWR</text>
          <text x="28" y="20" fill="#94a3b8" fontSize="4" fontFamily="monospace" textAnchor="middle">OUT</text>

          {/* Red Optical / Laser Beam shooting downwards across conveyor */}
          <g id="optical-beam">
            {/* Outer Soft Laser Glow */}
            <line
              x1="22"
              y1="42"
              x2="22"
              y2={42 + effectiveRange}
              stroke="#ef4444"
              strokeWidth="4"
              opacity={detected ? 0.25 : 0.4}
              strokeLinecap="round"
            />
            {/* Sharp Core Laser Beam */}
            <line
              x1="22"
              y1="42"
              x2="22"
              y2={42 + effectiveRange}
              stroke="#ffffff"
              strokeWidth="1.2"
              strokeDasharray={detected ? '3 3' : 'none'}
              opacity={detected ? 0.6 : 0.95}
              filter={`url(#laserGlow-${uid})`}
            />

            {/* Target Detection Spot or Reflector at Bottom */}
            <g transform={`translate(22, ${42 + effectiveRange})`}>
              {/* Retro-Reflector Target Block */}
              <rect x="-10" y="-1" width="20" height="5" rx="1" fill="#475569" stroke="#1e293b" strokeWidth="0.8" />
              {/* Honeycomb prismatic reflector surface */}
              <rect x="-8" y="0" width="16" height="3" fill="#cbd5e1" />
              <line x1="-6" y1="0" x2="-6" y2="3" stroke="#94a3b8" strokeWidth="0.5" />
              <line x1="-2" y1="0" x2="-2" y2="3" stroke="#94a3b8" strokeWidth="0.5" />
              <line x1="2" y1="0" x2="2" y2="3" stroke="#94a3b8" strokeWidth="0.5" />
              <line x1="6" y1="0" x2="6" y2="3" stroke="#94a3b8" strokeWidth="0.5" />

              {/* Laser Spot on Reflector (dimmed when beam is interrupted by product) */}
              <circle
                cx="0"
                cy="0"
                r={detected ? 1 : 2.5}
                fill="#ef4444"
                opacity={detected ? 0.2 : 1}
                filter={detected ? 'none' : `url(#laserGlow-${uid})`}
              />
            </g>
          </g>
        </g>
      )}

      {/* ================= 2. INDUCTIVE PROXIMITY SENSOR (M12/M18) ================= */}
      {variant === 'inductive' && (
        <g id="inductive-sensor" transform="translate(14, 0)">
          {/* Threaded Stainless Steel Barrel */}
          <rect
            x="0"
            y="6"
            width="16"
            height="36"
            fill={`url(#steelBarrelGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.8"
          />

          {/* Thread Ribs along the barrel */}
          {[10, 14, 18, 22, 26, 30, 34].map((ty) => (
            <line key={ty} x1="0" y1={ty} x2="16" y2={ty} stroke="#334155" strokeWidth="0.8" />
          ))}

          {/* Upper Mounting Nut */}
          <rect x="-3" y="12" width="22" height="5" rx="1" fill={`url(#brassNutGrad-${uid})`} stroke="#451a03" strokeWidth="0.8" />
          {/* Lower Mounting Nut */}
          <rect x="-3" y="24" width="22" height="5" rx="1" fill={`url(#brassNutGrad-${uid})`} stroke="#451a03" strokeWidth="0.8" />

          {/* Sensing Face (PBT Cap) facing downward */}
          <path
            d="M 1 42 L 15 42 L 13 47 L 3 47 Z"
            fill={detected ? '#f97316' : '#0284c7'}
            stroke="#0f172a"
            strokeWidth="0.8"
          />

          {/* Rear 360° Indicator LED Ring at the Top */}
          <rect
            x="1"
            y="2"
            width="14"
            height="4"
            rx="1"
            fill={detected ? '#f59e0b' : '#475569'}
            stroke="#0f172a"
            strokeWidth="0.6"
            filter={detected ? `url(#laserGlow-${uid})` : 'none'}
          />

          {/* Cable Bushing / Boot */}
          <rect x="5" y="-3" width="6" height="5" rx="1" fill="#020617" />

          {/* Electromagnetic Sensing Field Lines (when active/sensing) */}
          <g transform="translate(8, 47)">
            {/* Pulsing Arc Field Waves */}
            <path
              d="M -6 6 A 8 6 0 0 0 6 6"
              fill="none"
              stroke={detected ? '#f59e0b' : '#38bdf8'}
              strokeWidth={detected ? '2' : '1'}
              opacity={detected ? 0.9 : 0.3}
            />
            <path
              d="M -12 14 A 15 10 0 0 0 12 14"
              fill="none"
              stroke={detected ? '#f59e0b' : '#38bdf8'}
              strokeWidth={detected ? '2.5' : '1'}
              opacity={detected ? 0.8 : 0.2}
              strokeDasharray={detected ? 'none' : '2 2'}
            />
            <path
              d="M -18 24 A 22 15 0 0 0 18 24"
              fill="none"
              stroke={detected ? '#f59e0b' : '#38bdf8'}
              strokeWidth={detected ? '3' : '1'}
              opacity={detected ? 0.7 : 0.15}
            />
          </g>
        </g>
      )}

      {/* ================= 3. CAPACITIVE PROXIMITY SENSOR ================= */}
      {variant === 'capacitive' && (
        <g id="capacitive-sensor" transform="translate(12, 0)">
          {/* M18/M30 Heavy-Duty Plastic/Metal Threaded Barrel */}
          <rect
            x="0"
            y="6"
            width="20"
            height="38"
            rx="1"
            fill={`url(#steelBarrelGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.8"
          />

          {/* Thread Ribs */}
          {[11, 15, 19, 23, 27, 31, 35].map((ty) => (
            <line key={ty} x1="0" y1={ty} x2="20" y2={ty} stroke="#334155" strokeWidth="0.8" />
          ))}

          {/* Brass Nuts */}
          <rect x="-3" y="14" width="26" height="5.5" rx="1" fill={`url(#brassNutGrad-${uid})`} stroke="#451a03" strokeWidth="0.8" />
          <rect x="-3" y="26" width="26" height="5.5" rx="1" fill={`url(#brassNutGrad-${uid})`} stroke="#451a03" strokeWidth="0.8" />

          {/* Flat Sensing Face */}
          <rect x="2" y="44" width="16" height="4" rx="1" fill={detected ? '#eab308' : '#64748b'} stroke="#0f172a" strokeWidth="0.8" />

          {/* Sensitivity Trimpot on Rear */}
          <circle cx="10" cy="3" r="3" fill="#0f172a" stroke="#cbd5e1" strokeWidth="0.6" />
          <line x1="8.5" y1="3" x2="11.5" y2="3" stroke="#facc15" strokeWidth="0.8" />

          {/* LED Indicator Ring */}
          <circle cx="16" cy="3" r="1.8" fill={detected ? '#f59e0b' : '#475569'} filter={detected ? `url(#laserGlow-${uid})` : 'none'} />

          {/* Dielectric Capacitance Field */}
          <g transform="translate(10, 48)">
            <ellipse
              cx="0"
              cy="10"
              rx={detected ? 16 : 10}
              ry={detected ? 8 : 5}
              fill={detected ? 'rgba(234, 179, 8, 0.25)' : 'rgba(56, 189, 248, 0.1)'}
              stroke={detected ? '#eab308' : '#38bdf8'}
              strokeWidth={detected ? '1.8' : '1'}
              strokeDasharray={detected ? 'none' : '3 2'}
            />
          </g>
        </g>
      )}

      {/* ================= 4. OPTICAL LIGHT CURTAIN (IŞIK BARİYERİ) ================= */}
      {variant === 'light_curtain' && (
        <g id="light-curtain">
          {/* TX Tower (Left Post) */}
          <g transform="translate(6, 0)">
            <rect x="0" y="6" width="12" height={40 + effectiveRange} rx="2" fill={`url(#curtainPoleGrad-${uid})`} stroke="#451a03" strokeWidth="1" />
            {/* Top Indicator Lamp Head */}
            <circle cx="6" cy="4" r="3.5" fill={detected ? '#ef4444' : '#22c55e'} filter={`url(#laserGlow-${uid})`} />
            {/* Optical Windows */}
            {[0.2, 0.4, 0.6, 0.8].map((pct, idx) => (
              <rect key={idx} x="2" y={14 + pct * effectiveRange} width="8" height="4" rx="1" fill="#0f172a" stroke="#713f12" strokeWidth="0.5" />
            ))}
          </g>

          {/* RX Tower (Right Post) */}
          <g transform={`translate(${6 + 60}, 0)`}>
            <rect x="0" y="6" width="12" height={40 + effectiveRange} rx="2" fill={`url(#curtainPoleGrad-${uid})`} stroke="#451a03" strokeWidth="1" />
            <circle cx="6" cy="4" r="3.5" fill={detected ? '#ef4444' : '#22c55e'} filter={`url(#laserGlow-${uid})`} />
            {[0.2, 0.4, 0.6, 0.8].map((pct, idx) => (
              <rect key={idx} x="2" y={14 + pct * effectiveRange} width="8" height="4" rx="1" fill="#0f172a" stroke="#713f12" strokeWidth="0.5" />
            ))}
          </g>

          {/* Parallel Multi-Beam Laser Array across the conveyor */}
          {[0.2, 0.4, 0.6, 0.8].map((pct, idx) => {
            const beamY = 16 + pct * effectiveRange;
            return (
              <g key={idx}>
                <line
                  x1="18"
                  y1={beamY}
                  x2="66"
                  y2={beamY}
                  stroke="#ef4444"
                  strokeWidth="1.5"
                  strokeDasharray={detected ? '4 3' : 'none'}
                  opacity={detected ? 0.35 : 0.85}
                  filter={`url(#laserGlow-${uid})`}
                />
              </g>
            );
          })}
        </g>
      )}
    </svg>
  );
};
